import { Button, Grid, TextField, MenuItem } from '@mui/material';
import React, { useState, useContext, useEffect } from 'react';
import styles from './styles.module.css';
import CloseIcon from '@mui/icons-material/Close';
import KitchenService from 'services/KitchenService';
import { WarehouseContext } from 'Context/WarehouseContext';
import { DialogActions } from '@mui/material';
import { AlertMessagesContext } from 'react-alert-messages';
import Loader from 'components/utils/Loading';
import { useTranslation } from 'react-i18next';
import { ShopContext } from 'Context/ShopContext';
import { featureIsExpired } from 'components/utils/FeatureValidityExpire';
import { isValidIp } from 'helpers/StringHelper';

export default function EditKitchenDialog(props) {
  const { closeDialog, editData, reloadKitchens, salespersons = [] } = props;
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const { t: tCommon } = useTranslation('common');
  const { t: tPopup } = useTranslation('popup');
  const { t: tManagement } = useTranslation('management');

  const { getWarehouses, warehouses } = useContext(WarehouseContext);
  const { shop } = useContext(ShopContext);

  const isWarehouseAvailable = shop && !featureIsExpired(shop?.warehouse_validity);
  const isKotAvailable = shop && (!featureIsExpired(shop?.kot_validity) || !featureIsExpired(shop?.kot_lite_validity));

  const [warehouseId, setWarehouseId] = useState(editData?.warehouse?.id || '');
  const [name, setName] = useState(editData?.name || '');
  const [chefId, setChefId] = useState(editData?.chef?.id || '');
  const [printerIp, setPrinterIp] = useState(editData?.printer_ip || '');
  const [ipError, setIpError] = useState('');
  const [loadingIndicator, setLoadingIndicator] = useState(false);

  useEffect(() => {
    getWarehouses();
  }, [getWarehouses]);

  useEffect(() => {
    if (!isWarehouseAvailable && warehouses && warehouses.length > 0 && !warehouseId) {
      setWarehouseId(warehouses[0].id);
    }
  }, [isWarehouseAvailable, warehouses, warehouseId]);

  const handleSaveKitchen = async () => {
    if (!warehouseId) {
      postAlertMessage({
        text: tPopup('Please select a warehouse'),
        type: 'failed',
      });
      return;
    }
    if (!name || name.trim() === '') {
      postAlertMessage({
        text: tPopup('Please enter kitchen name'),
        type: 'failed',
      });
      return;
    }
    if (isKotAvailable && printerIp && printerIp.trim() !== '' && !isValidIp(printerIp.trim())) {
      setIpError(tCommon('Please enter a valid IP address') || 'Please enter a valid IP address');
      postAlertMessage({
        text: tCommon('Please enter a valid IP address') || 'Please enter a valid IP address',
        type: 'failed',
      });
      return;
    }
    setIpError('');
    const data = {
      warehouse_id: Number(warehouseId),
      name: name.trim(),
      chef_id: chefId ? Number(chefId) : null,
      printer_ip: isKotAvailable ? (printerIp.trim() || null) : null,
    };
    setLoadingIndicator(true);
    try {
      if (editData?.id) {
        await KitchenService.updateKitchen(editData.id, data);
        postAlertMessage({
          text: tPopup('Kitchen updated successfully') || 'Kitchen updated successfully',
          type: 'success',
        });
      } else {
        await KitchenService.createKitchen(data);
        postAlertMessage({
          text: tPopup('Kitchen created successfully') || 'Kitchen created successfully',
          type: 'success',
        });
      }
      reloadKitchens();
      closeDialog();
    } catch (error) {
      postAlertMessage({ text: error.message, type: 'failed' });
    }
    setLoadingIndicator(false);
  };

  return (
    <div className={styles.popUp}>
      <div className={styles.contentWrapper}>
        <Loader isOpen={loadingIndicator} />
        <div className={styles.headSec}>
          <h2 className={styles.editTitle}>
            {editData?.id ? tCommon('Edit Kitchen') || 'Edit Kitchen' : tCommon('Add Kitchen') || 'Add Kitchen'}
          </h2>
          <CloseIcon onClick={closeDialog} style={{ cursor: 'pointer' }} />
        </div>
        <div className={styles.inputSec}>
          <Grid container spacing={2}>
            {editData?.id && (
              <Grid item xs={12} sm={isWarehouseAvailable ? 6 : 12}>
                <TextField
                  label={tCommon('Kitchen ID') || 'Kitchen ID'}
                  variant="outlined"
                  size="small"
                  fullWidth
                  disabled
                  value={editData.id}
                />
              </Grid>
            )}
            {isWarehouseAvailable && (
              <Grid item xs={12} sm={editData?.id ? 6 : 12}>
                <TextField
                  select
                  label={tCommon('Warehouse') || 'Warehouse'}
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={warehouseId}
                  onChange={(e) => setWarehouseId(e.target.value)}
                  required
                >
                  {warehouses.map((wh) => (
                    <MenuItem key={wh.id} value={wh.id}>
                      {wh.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                label={tCommon('Kitchen Name')}
                variant="outlined"
                size="small"
                fullWidth
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={isKotAvailable ? 6 : 12}>
              <TextField
                select
                label={tCommon('Chef') || 'Chef'}
                variant="outlined"
                size="small"
                fullWidth
                value={chefId}
                onChange={(e) => setChefId(e.target.value)}
                required
              >
                {salespersons.length === 0 ? (
                  <MenuItem disabled value="">
                    {tManagement('No salespersons to list')}
                  </MenuItem>
                ) : (
                  salespersons.map((sp) => (
                    <MenuItem key={sp.id} value={sp.id}>
                      {sp.name}
                    </MenuItem>
                  ))
                )}
              </TextField>
            </Grid>
            {isKotAvailable && (
              <Grid item xs={12} sm={6}>
                <TextField
                  label={tCommon('Printer IP')}
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={printerIp}
                  onChange={(e) => {
                    setPrinterIp(e.target.value);
                    if (ipError) setIpError('');
                  }}
                  error={Boolean(ipError)}
                  helperText={ipError}
                  placeholder="e.g. 192.168.1.100"
                />
              </Grid>
            )}
          </Grid>
        </div>
        <div className={styles.footSec}>
          <DialogActions>
            <Button variant="contained" color="secondary" size="small" onClick={closeDialog}>
              {tCommon('Close')}
            </Button>
            <Button
              variant="contained"
              size="small"
              color="primary"
              disabled={loadingIndicator}
              onClick={handleSaveKitchen}
            >
              {editData?.id ? tCommon('Update Kitchen') : tCommon('Add Kitchen')}
            </Button>
          </DialogActions>
        </div>
      </div>
    </div>
  );
}
